// Ported from compose_v2's AiUsagePage, restyled with WYRM's Tailwind classes.
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import { fetchAiUsage } from '../services/admin';

const integer = new Intl.NumberFormat('en-GB');
const currency = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'USD', minimumFractionDigits: 4 });
const currencyShort = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const seconds = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 });

const fmtNumber = (value) => integer.format(Number(value || 0));
const fmtCost = (value) => (Number(value || 0) > 0 ? currency.format(Number(value)) : '-');
const fmtMs = (value) => (value ? `${fmtNumber(value)} ms` : '-');
const fmtWhen = (value) =>
  value ? new Date(`${value}Z`).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';

const PROVIDER_LABELS = { azure_openai: 'Azure OpenAI', anthropic: 'Anthropic', local_llamacpp: 'Local llama.cpp' };
const OPERATION_LABELS = {
  'meeting.summary': 'Minutes',
  'meeting.summary_retry': 'Minutes (shorter retry)',
  'meeting.chat': 'Meeting chat',
  'meeting.speaker_id': 'Speaker naming',
  'dictation.correction': 'Dictation correction',
};

const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const thNum = `${th} text-right`;
const td = 'px-3 py-2 text-sm text-gray-900 whitespace-nowrap';
const tdNum = `${td} text-right tabular-nums`;

const Figure = ({ label, value, note }) => (
  <div className="bg-white rounded-lg shadow-md p-4">
    <dt className="text-sm text-gray-500">{label}</dt>
    <dd className="text-2xl font-semibold text-gray-900">{value}</dd>
    {note ? <p className="text-xs text-gray-500 mt-1">{note}</p> : null}
  </div>
);

const AiUsagePage = () => {
  const [usage, setUsage] = useState({ totals: {}, by_model: [], recent_events: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      setUsage(await fetchAiUsage(100));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load AI usage');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = usage.totals || {};
  const byModel = usage.by_model || [];
  const recentEvents = usage.recent_events || [];
  const totalTokens = Number(totals.total_tokens || 0);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI usage</h1>
          <p className="text-gray-600">
            Tokens, time and estimated cost of the model calls for minutes, meeting chat, speaker naming and dictation.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={16} aria-hidden="true" /> Refresh
        </button>
      </div>

      <dl className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" aria-busy={loading}>
        <Figure label="Model calls" value={fmtNumber(totals.event_count)} />
        <Figure
          label="Tokens"
          value={fmtNumber(totals.total_tokens)}
          note={`${fmtNumber(totals.input_tokens)} in (${fmtNumber(totals.cached_input_tokens)} cached) · ${fmtNumber(totals.output_tokens)} out`}
        />
        <Figure
          label="Estimated cost"
          value={Number(totals.estimated_cost_usd || 0) > 0 ? currencyShort.format(Number(totals.estimated_cost_usd)) : '-'}
          note="Estimate from the rates in Settings"
        />
        <Figure
          label="Average model time"
          value={totals.average_duration_ms ? `${seconds.format(Number(totals.average_duration_ms) / 1000)} s` : '-'}
        />
      </dl>

      <section className="bg-white rounded-lg shadow-md overflow-hidden mb-6" aria-labelledby="usage-by-model-heading">
        <h2 id="usage-by-model-heading" className="px-5 py-4 border-b border-gray-200 text-xl font-semibold text-gray-900">
          By model
        </h2>
        {byModel.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={th}>Model</th>
                  <th scope="col" className={th}>Provider</th>
                  <th scope="col" className={thNum}>Calls</th>
                  <th scope="col" className={thNum}>Input</th>
                  <th scope="col" className={thNum}>Cached</th>
                  <th scope="col" className={thNum}>Output</th>
                  <th scope="col" className={thNum}>Share of tokens</th>
                  <th scope="col" className={thNum}>Avg time</th>
                  <th scope="col" className={thNum}>Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {byModel.map((row) => (
                  <tr key={`${row.provider}-${row.model_name}`}>
                    <td className={td}><code>{row.model_name || '-'}</code></td>
                    <td className={td}>{PROVIDER_LABELS[row.provider] || row.provider}</td>
                    <td className={tdNum}>{fmtNumber(row.event_count)}</td>
                    <td className={tdNum}>{fmtNumber(row.input_tokens)}</td>
                    <td className={tdNum}>{fmtNumber(row.cached_input_tokens)}</td>
                    <td className={tdNum}>{fmtNumber(row.output_tokens)}</td>
                    <td className={tdNum}>
                      {totalTokens ? Math.round((Number(row.total_tokens || 0) / totalTokens) * 100) : 0}%
                    </td>
                    <td className={tdNum}>{fmtMs(row.average_duration_ms)}</td>
                    <td className={tdNum}>{fmtCost(row.estimated_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-5 py-4 text-gray-600">
            {loading ? 'Loading...' : 'No model calls recorded yet. Usage appears here after the first minutes are generated.'}
          </p>
        )}
      </section>

      {recentEvents.length ? (
        <section className="bg-white rounded-lg shadow-md overflow-hidden" aria-labelledby="usage-recent-heading">
          <h2 id="usage-recent-heading" className="px-5 py-4 border-b border-gray-200 text-xl font-semibold text-gray-900">
            Recent calls
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={th}>When</th>
                  <th scope="col" className={th}>User</th>
                  <th scope="col" className={th}>Meeting</th>
                  <th scope="col" className={th}>Model</th>
                  <th scope="col" className={th}>For</th>
                  <th scope="col" className={thNum}>Input</th>
                  <th scope="col" className={thNum}>Output</th>
                  <th scope="col" className={thNum}>Model time</th>
                  <th scope="col" className={thNum}>Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {recentEvents.map((event) => (
                  <tr key={event.id}>
                    <td className={td}>{fmtWhen(event.created_at)}</td>
                    <td className={td}>{event.user_id || '-'}</td>
                    <td className={td}>{event.meeting_session_id ?? '-'}</td>
                    <td className={td}><code>{event.model_name}</code></td>
                    <td className={td} title={event.operation}>{OPERATION_LABELS[event.operation] || event.operation}</td>
                    <td className={tdNum}>{fmtNumber(event.input_tokens)}</td>
                    <td className={tdNum}>{fmtNumber(event.output_tokens)}</td>
                    <td className={tdNum}>{fmtMs(event.duration_ms)}</td>
                    <td className={tdNum}>{fmtCost(event.estimated_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default AiUsagePage;
